/**
 * Calculadora de frete (bloco "shipping_calculator") + tag de prazo de
 * entrega (bloco "delivery_estimate") de panda-produto-main.liquid.
 *
 * A calculadora salva a escolha de estado em localStorage e emite o evento
 * "panda:shipping-resolved" — a tag de prazo escuta esse evento (e também
 * lê o localStorage já no load) pra combinar dias de produção + dias de
 * envio reais do estado escolhido numa data estimada.
 *
 * Se o bloco tiver "data-auto-detect", tenta pré-preencher o estado sozinho
 * via geolocalização por IP (ipwho.is, sem chave, gratuito) assim que a
 * página carrega — sem exigir nenhum clique do cliente. Qualquer falha
 * (rede, timeout, fora do Brasil, estado sem correspondência na tabela)
 * simplesmente deixa a calculadora no estado manual original — é só um
 * atalho, nunca um requisito.
 */
(function () {
  'use strict';

  var STORAGE_KEY = 'panda_shipping_estimate';
  var GEO_ENDPOINT = 'https://ipwho.is/';
  var GEO_TIMEOUT_MS = 2500;
  var geoLookupPromise = null;

  function readStoredShipping() {
    try {
      var raw = window.localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch (e) {
      return null;
    }
  }

  function writeStoredShipping(data) {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    } catch (e) {
      /* localStorage indisponível (modo privado, cookies bloqueados etc.) —
         segue sem cache, só não persiste entre páginas. */
    }
  }

  function formatBRL(value) {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
  }

  function addBusinessDays(startDate, days) {
    var result = new Date(startDate.getTime());
    var added = 0;
    while (added < days) {
      result.setDate(result.getDate() + 1);
      var day = result.getDay();
      if (day !== 0 && day !== 6) added += 1;
    }
    return result;
  }

  function formatEstimateDate(totalDays) {
    var date = addBusinessDays(new Date(), totalDays);
    return date.toLocaleDateString('pt-BR', { day: 'numeric', month: 'long' });
  }

  function updateDeliveryEstimates(resolved) {
    document.querySelectorAll('[data-delivery-estimate]').forEach(function (el) {
      var textEl = el.querySelector('[data-delivery-estimate-text]');
      if (!textEl) return;
      var productionDays = parseInt(el.getAttribute('data-production-days'), 10) || 0;
      var shippingMax = resolved
        ? resolved.diasMax
        : parseInt(el.getAttribute('data-shipping-max'), 10) || 0;
      var template = el.getAttribute('data-template') || 'Chegue até [data][sigla]';
      var dateLabel = formatEstimateDate(productionDays + shippingMax);
      // [sigla] só existe depois que a calculadora resolve um estado (não
      // dá pra saber a sigla antes disso) — some sozinho enquanto isso.
      var siglaLabel = resolved && resolved.uf ? ' (' + resolved.uf + ')' : '';
      textEl.textContent = template.replace('[data]', dateLabel).replace('[sigla]', siglaLabel);
    });
  }

  // Lê os dados de um <option> da tabela de estados — usado tanto na
  // escolha manual quanto na detecção automática, pra que os dois caminhos
  // sempre mostrem exatamente o mesmo nome/prazo/preço configurado pelo
  // lojista (nunca um texto "cru" vindo direto da API de geolocalização).
  function resolveFromOption(option) {
    return {
      uf: option.value,
      local: option.getAttribute('data-nome') || '',
      diasMin: parseInt(option.getAttribute('data-dias-min'), 10) || 0,
      diasMax: parseInt(option.getAttribute('data-dias-max'), 10) || 0,
      preco: parseFloat(option.getAttribute('data-preco')) || 0
    };
  }

  function renderResolved(widget, resolved) {
    var triggerText = widget.querySelector('[data-shipping-calc-trigger-main]');
    var recalcEl = widget.querySelector('[data-shipping-calc-recalc]');
    var select = widget.querySelector('[data-shipping-calc-select]');
    var template = widget.getAttribute('data-result-template') || 'Frete pra [local]: [preco] · chega em [dias] dias úteis';
    var freeLabel = widget.getAttribute('data-free-label') || 'Grátis';
    // Não usa "|| 'Recalcular'" aqui: se o lojista esvaziar o campo de
    // propósito (quer esconder o aviso), o atributo existe mas fica ""
    // (falsy) — "||" trocaria de volta pro texto padrão, ignorando a
    // intenção de deixar em branco. getAttribute só retorna null se o
    // atributo nem existisse.
    var recalcLabelAttr = widget.getAttribute('data-recalculate-label');
    var recalcLabel = recalcLabelAttr !== null ? recalcLabelAttr : 'Recalcular';
    var precoLabel = resolved.preco > 0 ? formatBRL(resolved.preco) : freeLabel;
    var diasLabel = resolved.diasMin + '-' + resolved.diasMax;
    var text = template
      .replace('[local]', resolved.local || '')
      .replace('[preco]', precoLabel)
      .replace('[dias]', diasLabel);
    if (triggerText) triggerText.textContent = text;
    if (recalcEl) recalcEl.textContent = resolved.auto ? recalcLabel : '';
    if (select) select.value = resolved.uf;
    widget.classList.add('panda-is-resolved');
    widget.classList.remove('panda-is-open');
    widget.classList.toggle('panda-is-auto-resolved', !!resolved.auto);
  }

  function fetchGeoLocationOnce() {
    if (!geoLookupPromise) {
      geoLookupPromise = new Promise(function (resolve) {
        var settled = false;
        var timer = setTimeout(function () {
          if (settled) return;
          settled = true;
          resolve(null);
        }, GEO_TIMEOUT_MS);

        fetch(GEO_ENDPOINT)
          .then(function (res) { return res.json(); })
          .then(function (data) {
            if (settled) return;
            settled = true;
            clearTimeout(timer);
            resolve(data);
          })
          .catch(function () {
            if (settled) return;
            settled = true;
            clearTimeout(timer);
            resolve(null);
          });
      });
    }
    return geoLookupPromise;
  }

  function tryAutoDetect(widget) {
    if (!widget.hasAttribute('data-auto-detect')) return;
    if (readStoredShipping()) return; // já resolvido antes (manual ou automático) — não sobrescrever
    var select = widget.querySelector('[data-shipping-calc-select]');
    if (!select) return;

    fetchGeoLocationOnce().then(function (data) {
      if (!data || data.success === false || data.country_code !== 'BR') return;
      if (readStoredShipping()) return; // o cliente já escolheu manualmente enquanto a API respondia
      var uf = data.region_code;
      var option = null;
      for (var i = 0; i < select.options.length; i += 1) {
        if (select.options[i].value === uf) {
          option = select.options[i];
          break;
        }
      }
      if (!option) return; // estado fora da tabela configurada — fica no manual

      var resolved = resolveFromOption(option);
      resolved.auto = true;
      writeStoredShipping(resolved);
      renderResolved(widget, resolved);
      updateDeliveryEstimates(resolved);
      document.dispatchEvent(new CustomEvent('panda:shipping-resolved', { detail: resolved }));
    });
  }

  function initShippingCalc(widget) {
    var trigger = widget.querySelector('[data-shipping-calc-trigger]');
    var select = widget.querySelector('[data-shipping-calc-select]');

    if (trigger) {
      trigger.addEventListener('click', function (event) {
        event.stopPropagation();
        widget.classList.toggle('panda-is-open');
      });
    }

    // O painel agora é um popup flutuante (position:absolute), não um
    // accordion — precisa fechar ao clicar fora ou apertar Esc, senão fica
    // flutuando aberto por cima do resto da página indefinidamente.
    document.addEventListener('click', function (event) {
      if (widget.classList.contains('panda-is-open') && !widget.contains(event.target)) {
        widget.classList.remove('panda-is-open');
      }
    });
    document.addEventListener('keydown', function (event) {
      if (event.key === 'Escape') widget.classList.remove('panda-is-open');
    });

    if (select) {
      select.addEventListener('change', function () {
        var option = select.options[select.selectedIndex];
        if (!option || !option.value) return;
        var resolved = resolveFromOption(option);
        resolved.auto = false;
        writeStoredShipping(resolved);
        renderResolved(widget, resolved);
        updateDeliveryEstimates(resolved);
        document.dispatchEvent(new CustomEvent('panda:shipping-resolved', { detail: resolved }));
      });
    }

    var stored = readStoredShipping();
    if (stored) {
      renderResolved(widget, stored);
    } else {
      tryAutoDetect(widget);
    }
  }

  document.addEventListener('DOMContentLoaded', function () {
    document.querySelectorAll('[data-shipping-calc]').forEach(initShippingCalc);
    updateDeliveryEstimates(readStoredShipping());
  });

  document.addEventListener('panda:shipping-resolved', function (event) {
    updateDeliveryEstimates(event.detail);
  });
})();
